/**
 * Estabelecimento Form Dialog Component
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Estabelecimento } from "@/types/estabelecimento";
import { estabelecimentoSchema, EstabelecimentoFormData } from "@/schemas/estabelecimentoSchema";

interface EstabelecimentoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  estabelecimento: Estabelecimento | null;
  onSubmit: (data: EstabelecimentoFormData) => void;
  isLoading?: boolean;
}

export function EstabelecimentoFormDialog({
  open,
  onOpenChange,
  mode,
  estabelecimento,
  onSubmit,
  isLoading = false,
}: EstabelecimentoFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EstabelecimentoFormData>({
    resolver: zodResolver(estabelecimentoSchema),
    defaultValues: {
      ativo: true,
    },
  });

  const ativo = watch("ativo");

  useEffect(() => {
    if (open) {
      if (mode === "edit" && estabelecimento) {
        reset({
          nome: estabelecimento.nome,
          cnpj: estabelecimento.cnpj || "",
          responsavel: estabelecimento.responsavel || "",
          telefone: estabelecimento.telefone || "",
          email: estabelecimento.email || "",
          endereco: estabelecimento.endereco || "",
          observacoes: estabelecimento.observacoes || "",
          ativo: estabelecimento.ativo,
        });
      } else {
        reset({
          nome: "",
          cnpj: "",
          responsavel: "",
          telefone: "",
          email: "",
          endereco: "",
          observacoes: "",
          ativo: true,
        });
      }
    }
  }, [open, mode, estabelecimento, reset]);

  const handleFormSubmit = (data: EstabelecimentoFormData) => {
    onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Estabelecimento" : "Editar Estabelecimento"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input id="nome" {...register("nome")} />
              {errors.nome && (
                <p className="text-sm text-destructive mt-1">{errors.nome.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input id="cnpj" {...register("cnpj")} placeholder="00.000.000/0000-00" />
              {errors.cnpj && (
                <p className="text-sm text-destructive mt-1">{errors.cnpj.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input id="telefone" {...register("telefone")} placeholder="(00) 00000-0000" />
              {errors.telefone && (
                <p className="text-sm text-destructive mt-1">{errors.telefone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" {...register("email")} />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="responsavel">Responsável</Label>
              <Input id="responsavel" {...register("responsavel")} />
              {errors.responsavel && (
                <p className="text-sm text-destructive mt-1">{errors.responsavel.message}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="endereco">Endereço</Label>
              <Input id="endereco" {...register("endereco")} />
              {errors.endereco && (
                <p className="text-sm text-destructive mt-1">{errors.endereco.message}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea id="observacoes" {...register("observacoes")} rows={3} />
              {errors.observacoes && (
                <p className="text-sm text-destructive mt-1">{errors.observacoes.message}</p>
              )}
            </div>

            <div className="col-span-2 flex items-center space-x-2">
              <Switch
                id="ativo"
                checked={ativo}
                onCheckedChange={(checked) => setValue("ativo", checked)}
              />
              <Label htmlFor="ativo">Estabelecimento Ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
